import { FooterResponseModel, OneSectionModel } from "@models/footer";
import { getFirstIfArray } from "@utils/common";

import { Link, graphql, useStaticQuery } from "gatsby";
import React from "react";
import UnbxdLogoText from "@assets/images/svg/unbxd_logo_text.svg";
import dayjs from "dayjs";
import { footerSocialLinks } from "@constants/footer";
import Button from "../Button";
import { twMerge } from "tailwind-merge";

const Footer: React.FunctionComponent = () => {
  const fullYear = dayjs().format("YYYY");
  const data = useStaticQuery<FooterResponseModel>(graphql`
    query footerQuery {
      allStrapiFooter {
        edges {
          node {
            id
            Sections {
              id
              title
              Links {
                id
                title
                link
              }
            }
          }
        }
      }
    }
  `);

  const { Sections } = getFirstIfArray(data?.allStrapiFooter.edges).node;
  const solutionSection = Sections.filter((sec) => sec.title === "Solutions");
  const restSection = Sections.filter((sec) => sec.title !== "Solutions");
  const renderOneSection = (
    section: OneSectionModel,
    isSolutionSection: boolean
  ) => {
    const { title, Links } = section;
    const wrapperClassName = isSolutionSection
      ? "border-b border-opacity-10 border-grey-80"
      : "";
    const linkClassName = isSolutionSection ? "grid grid-cols-3" : "";
    return (
      <div className="mb-3 md:mb-0">
        <div
          className={twMerge(
            "text-xl font-bold md:mb-7 pb-2",
            wrapperClassName
          )}
        >
          {title}
        </div>
        <div className={twMerge("", linkClassName)}>
          {Links.map((link) => (
            <div
              key={link.id}
              className="mb-2 text-sm md:text-lg font-medium opacity-80"
            >
              <Link to={link.link ?? "#"}>{link.title}</Link>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderLogo = () => (
    <span className="hidden md:inline">
      <UnbxdLogoText />
    </span>
  );

  const renderCopyRight = () => (
    <div className="font-normal opacity-80">
      © Copyright {fullYear}, All Rights Reserved by Netcore UNBXD
    </div>
  );

  const renderSocialMediaLinks = () => {
    return (
      <div className="flex mt-3 md:mt-0">
        {footerSocialLinks.map((socialLink) => {
          const { id, Icon, link } = socialLink;
          return (
            <Button
              key={id}
              variant="rounded"
              className="w-8 h-8 mx-3 md:mx-0 md:ml-8 bg-dark-purple-30 hover:bg-primary-orange-50"
            >
              <a
                href={link}
                referrerPolicy="no-referrer"
                target="_blank"
                rel="noreferrer"
              >
                <Icon size={18} />
              </a>
            </Button>
          );
        })}
      </div>
    );
  };

  return (
    <footer className="flex min-h-[528px] bg-dark-purple-50 px-4 md:px-32 pt-20 text-white flex-col justify-between pb-8 ">
      <div className="xl:grid grid-cols-7 gap-8">
        <div className="col-span-3 mb-5 md:mb-3">
          {solutionSection.map((oneSection) => (
            <div key={oneSection.id}>{renderOneSection(oneSection, true)}</div>
          ))}
        </div>
        <div className="grid grid-cols-2 2xl:grid-cols-4 col-span-4 gap-5">
          {restSection.map((oneSection) => (
            <div key={oneSection.id}>{renderOneSection(oneSection, false)}</div>
          ))}
        </div>
      </div>
      <hr style={{ opacity: 0.1 }} />
      <div className="flex flex-col items-center md:flex md:flex-row md:justify-between md:items-start pt-3">
        {renderLogo()}
        {renderCopyRight()}
        {renderSocialMediaLinks()}
      </div>
    </footer>
  );
};

export default Footer;
